<template>
	<RegisterContainer
		:menu-items="contextualMenuItems"
		@back="() => $router.push({ path: '/' })"
		@next="sendOtp"
	>
		<h4
			class="font-weight-bold"
			:class="$isMobile() ? 'text-h6' : 'text-h4'"
		>
			{{ t('title') }}
		</h4>
		<h5 :class="$isMobile() ? 'text-body-1' : 'text-h5'">
			{{ t('subtitle') }}
		</h5>
		<h6
			id="1"
			class="my-10"
			:class="$isMobile() ? 'text-subtitle-1' : 'text-h6'"
		>
			{{ t('documents.requiredTitle') }} :
		</h6>
		<DocumentDescription :items="requiredDocuments" />
		<h6
			id="2"
			class="my-10"
			:class="$isMobile() ? 'text-subtitle-1' : 'text-h6'"
		>
			{{ t('documents.optionalTitle') }} :
		</h6>
		<DocumentDescription :items="optionalDocuments" />
		<h6
			class="my-10"
			:class="$isMobile() ? 'text-body-2' : 'text-body-1'"
		>
			{{ t('info') }}
		</h6>
		<h6
			id="3"
			class="my-10"
			:class="$isMobile() ? 'text-subtitle-1' : 'text-h6'"
		>
			{{ t('documents.bankTitle') }} :
		</h6>
		<DocumentDescription :items="bankDocuments" />
		<VAlert
			type="warning"
			class="my-10 text-body-1 warn"
			:class="$isMobile() ? 'pa-4' : 'pa-6'"
			outlined
			:prominent="!$isMobile()"
			color="yellow-darken-60"
			:icon="alertIcon"
		>
			{{ t('alert') }}
		</VAlert>
		<h6 class="text-h6 mb-6">
			{{ t('connection.title') }}
		</h6>
		<p class="text-body-1 mb-6">
			{{ t('connection.subtitle') }}
		</p>
		<VForm ref="form">
			<CardItem class="mb-12">
				<VTextField
					v-model="email"
					:label="t('connection.email')"
					:rules="emailRules"
					validate-on-blur
					outlined
					class="rounded mb-10"
					hide-details
					background-color="white"
					clearable
					:disabled="!!user.email"
					@blur="email = email.trim()"
				/>
				<VTextField
					v-model="confirmEmail"
					:label="t('connection.confirm')"
					:rules="emailRules"
					validate-on-blur
					outlined
					class="rounded"
					hide-details
					background-color="white"
					clearable
					:error="confirmEmailError"
					:disabled="!!user.email"
					@paste.prevent
					@blur="confirmEmail = confirmEmail.trim()"
				/>
			</CardItem>
			<div class="d-flex align-center mb-8">
				<VCheckbox
					v-model="checkbox"
					class="mr-4"
					:rules="checkboxRules"
					hide-details
				/>
				<div class="d-flex flex-column">
					<span :class="$isMobile() ? 'text-body-2' : 'text-body-1'">
						{{ t('confirm') }}
					</span>
					<span class="text-caption">
						{{ t('requiredField') }}
					</span>
				</div>
			</div>
		</VForm>
		<DialogBox
			v-model="dialogOtp"
			hide-actions
			max-width="560px"
			persistent
			:vuetify-options="{ card: { class: 'rounded-lg pa-8' } }"
		>
			<template #title>
				<BackBtn @click="dialogOtp = false">
					{{ t('dialog.back') }}
				</BackBtn>
			</template>
			<VCol align="center">
				<h6 class="text-h6">
					{{ t('dialog.title') }}
				</h6>
				<VSheet max-width="300px">
					<VOtpInput
						length="6"
						type="number"
						background-color="transparent"
						class="mt-6 mb-12"
						@finish="checkOtp"
					/>
				</VSheet>
				<p
					class="text-decoration-underline mb-0 pointer"
					@click="sendOtp"
				>
					{{ t('dialog.new') }}
				</p>
			</VCol>
		</DialogBox>
	</RegisterContainer>
</template>

<script lang="ts">
	import Vue from 'vue';
	import Component from 'vue-class-component';
	import { i18n } from '@/i18n';
	import { mapState, mapActions } from 'vuex';
	import { mdiAlertOutline } from '@mdi/js';
	import { required } from '@cnamts/vue-dot/src/rules/required';
	import { email } from '@cnamts/vue-dot/src/rules/email';
	import { MenuItem } from '@cnamts/vue-dot/src/patterns/ContextualMenu/types';
	import { Refs, VForm } from '@cnamts/vue-dot/src/types';
	import { StateEnum } from '@cnamts/vue-dot/src/constants/enums/StateEnum';

	import { DocumentDescriptionItem } from '@/types';
	import DocumentDescription from '@/components/DocumentDescription';
	import Summary from '@/components/SummaryView';
	import CardItem from '@/components/CardItem/CardItem.vue';
	import RegisterContainer from '@/components/RegisterContainer/RegisterContainer.vue';
	import { sendOtp, checkOtp } from '@/services/oauth/otp/api';
	import { attachmentMap } from '@/types/constants';

	@Component<Information>({
		computed: {
			...mapState('appState', ['user', 'code']),
			...mapState('registerState', ['informationBool'])
		},
		methods: {
			...mapActions('appState', ['updateUserEmail', 'updateAuthCode']),
			...mapActions('registerState', ['updateInformationBool']),
			...mapActions('notification', ['addNotification'])
		},
		components: {
			RegisterContainer,
			CardItem,
			DocumentDescription,
			Summary
		}
	})
	export default class Information extends Vue {
		requiredDocuments: DocumentDescriptionItem[] = [];
		optionalDocuments: DocumentDescriptionItem[] = [];
		bankDocuments: DocumentDescriptionItem[] = [];
		checkbox = false;
		checkboxError = false;
		alertIcon = mdiAlertOutline;
		email = '';
		confirmEmail = '';
		confirmEmailError = false;
		emailRules = [required, email];
		checkboxRules = [required];
		dialogOtp = false;
		StateEnum = StateEnum;
		sendState: StateEnum = StateEnum.IDLE;
		$refs!: Refs<{
			form: VForm;
		}>;
		contextualMenuItems: Array<MenuItem> = [
			{
				text: this.t('documents.requiredTitle'),
				hash: ''
			},
			{
				text: this.t('documents.optionalTitle'),
				hash: '#2'
			},
			{
				text: this.t('documents.bankTitle'),
				hash: '#3'
			}
		];

		t(path: string): string {
			return this.$t(`views.register.information.${path}`).toString();
		}

		async sendOtp(): Promise<void> {
			await this.$nextTick();
			if (!this.checkbox) {
				this.checkboxError = true;
				this.$refs.form.validate();
				return;
			}
			if (!this.$refs.form.validate()) {
				return;
			}
			if (this.email !== this.confirmEmail) {
				this.confirmEmailError = true;
				return;
			}
			if (this.code) {
				await this.$router.push({
					path: '/register/identification'
				});
				return;
			}
			this.sendState = StateEnum.PENDING;
			try {
				await sendOtp({
					email: this.email
				});
				this.sendState = StateEnum.RESOLVED;
				this.dialogOtp = true;
			} catch	 {
				this.sendState = StateEnum.REJECTED;
				this.addNotification({
					type: 'error',
					message: this.t('error') as string
				});
			}
			return;
		}

		async checkOtp(otp: string): Promise<void> {
			try {
				await checkOtp({
					email: this.email,
					code: parseInt(otp)
				});
				this.updateAuthCode(parseInt(otp));
				await this.$router.push({
					path: '/register/identification'
				});
			} catch {
				this.addNotification({
					type: 'error',
					message: this.t('errorOtp') as string
				});
			}

			return;
		}

		mounted() {
			window.scrollTo({ top: 10, behavior: 'smooth' });
			setTimeout(() => {
					window.scrollTo({ top: 0, behavior: 'smooth' });
				},
				300
			);

			this.$emit('set-step', 1);
			if (this.user.email)
				this.email = this.user.email;
			this.confirmEmail = this.email;
			this.checkbox = this.informationBool;
			const rules = this.user.rules;
			if (!rules)
				return;
			for (const key in attachmentMap) {
				if (rules.requiredAttachments.includes(key)) {
					let examplePath = null;
					if (key === 'FORM_S1')
						examplePath = `/documents/${key}.pdf`;
					else if (key === 'RESOURCE_CERTIFICATE' || key === 'PARENTAL_AUTHORIZATION')
						examplePath = `/documents/${key}_${i18n.locale}.pdf`;
					this.requiredDocuments.push({
						title: this.t(`documents.${key}.title`),
						content: this.t(`documents.${key}.content`),
						examplePath: examplePath,
						examplePathRequired: key === 'RESOURCE_CERTIFICATE' || key === 'PARENTAL_AUTHORIZATION'
					});
				}
			}
			for (const key in attachmentMap) {
				let examplePath = null;
				let array = this.optionalDocuments;
				if (key === 'BANK_DETAILS_DELEGATE' || key === 'BANK_DETAILS') {
					array = this.bankDocuments;
					examplePath = `/documents/BANK_DETAILS_DELEGATE_${i18n.locale}.pdf`;
				}
				if (rules.optionalAttachments.includes(key)) {
					array.push({
						title: this.t(`documents.${key}.title`),
						content: this.t(`documents.${key}.content`),
						examplePath: examplePath
					});
				}
			}
		}
		destroyed(): void {
			this.updateUserEmail(this.email);
			this.updateInformationBool(this.checkbox);
		}
	}
</script>

<style lang="scss" scoped>
@import '@cnamts/design-tokens/dist/tokens';

:deep(.warn) ::after {
  color: $vd-yellow-base;
}
</style>
